import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Phone, Loader2, ShieldCheck, Camera, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { SITE } from "../data/site";
import { getAttribution, dl, sha256Hex } from "../lib/tracking";
import { trackMetaLead } from "../lib/metaPixel";
import { API } from "@/lib/api";

const SCHULD_OPTIONEN = [
  { value: "nein", label: "Ja, unverschuldet" },
  { value: "teilweise", label: "Teilweise" },
  { value: "unklar", label: "Weiß nicht" },
];

// Hürdenarmes Rückruf-Formular: Schritt 1 (Name + Telefon + Schuldfrage) ist
// sofort absendbar, Schritt 2 (PLZ, Kennzeichen, Fotos, Wunschtermin) ist
// freiwillig und kann auch später nachgereicht werden.
export const RueckrufForm = ({ source = "rueckruf-formular", heading = "Kostenlosen Rückruf anfordern", subline = "Wir rufen Sie zurück – in der Regel innerhalb von 15 Minuten (Mo–Fr 9–22 Uhr). Kostenlos & unverbindlich.", testid = "rueckruf-form" }) => {
  const [form, setForm] = useState({ name: "", telefon: "", schuld: "" });
  const [extra, setExtra] = useState({ plz: "", kennzeichen: "", wunschtermin: "" });
  const [fotos, setFotos] = useState([]);
  const [leadId, setLeadId] = useState("");
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [started, setStarted] = useState(false);
  const [datenschutz, setDatenschutz] = useState(false);
  const navigate = useNavigate();

  const inputCls = "w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm text-navy placeholder:text-slate-400 focus:border-ember focus:ring-2 focus:ring-ember/20 outline-none transition-shadow";

  const markStart = () => {
    if (!started) {
      setStarted(true);
      dl({ event: "form_start", form_id: "rueckruf_form", page_path: window.location.pathname });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!datenschutz) { toast.error("Bitte bestätigen Sie die Datenschutzerklärung."); return; }
    setLoading(true);
    try {
      const parts = form.name.trim().split(/\s+/);
      const attr = getAttribution();
      const fd = new FormData();
      fd.append("vorname", parts[0] || form.name);
      fd.append("nachname", parts.slice(1).join(" "));
      fd.append("telefon", form.telefon);
      fd.append("schuldfrage", form.schuld);
      fd.append("quelle", source);
      fd.append("quell_url", window.location.pathname);
      ["utm_source", "utm_medium", "utm_campaign", "gclid", "gbraid", "wbraid"].forEach((k) => fd.append(k, attr[k] || ""));
      fd.append("landing_page", attr.landing_page || "");
      const res = await axios.post(`${API}/leads/schaden`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Messung: Meta Lead + dataLayer (Google-Ads-Conversion feuert zentral
      // über den Axios-Interceptor in lib/measurement.js mit transaction_id)
      trackMetaLead(source);
      const phoneHash = await sha256Hex(form.telefon);
      dl({
        event: "lead_form_submit",
        form_step: "complete",
        lead_type: "unfall",
        page_path: window.location.pathname,
        user_data: { phone_number: phoneHash || undefined },
      });
      toast.success("Rückruf angefordert – wir melden uns umgehend!");
      setLeadId(res.data?.id || "");
      setStep(2);
    } catch {
      toast.error(`Senden fehlgeschlagen. Bitte rufen Sie uns an: ${SITE.phone}`);
    } finally {
      setLoading(false);
    }
  };

  const submitExtra = async (e) => {
    e.preventDefault();
    if (!leadId) { navigate("/danke"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("plz", extra.plz);
      fd.append("kennzeichen", extra.kennzeichen);
      fd.append("wunschtermin", extra.wunschtermin);
      fotos.forEach((f) => fd.append("fotos", f));
      await axios.put(`${API}/leads/${leadId}/details`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      dl({ event: "lead_details_added", page_path: window.location.pathname });
    } catch { /* Nachtrag ist optional – trotzdem weiter zur Danke-Seite */ }
    setLoading(false);
    navigate("/danke");
  };

  return (
    <div data-testid={testid} className="rounded-2xl bg-white text-navy border border-slate-200 overflow-hidden shadow-2xl" style={{ boxShadow: "0 24px 60px -18px rgba(11,25,44,0.45)" }}>
      <div className="h-1.5 cta-gradient" />
      {step === 1 ? (
        <form onSubmit={submit} className="p-6 sm:p-7">
          <div className="flex items-center justify-between gap-2">
            <h2 className="font-display text-xl sm:text-2xl font-bold">{heading}</h2>
            <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full whitespace-nowrap">Antwort in ~15 Min.</span>
          </div>
          <p className="text-sm text-slate-500 mt-1.5">{subline}</p>
          <div className="mt-4 space-y-3">
            <div>
              <label htmlFor={`${testid}-name`} className="text-xs font-semibold uppercase tracking-wider text-slate-500">Ihr Name *</label>
              <input id={`${testid}-name`} required data-testid={`${testid}-name-input`} value={form.name} onChange={(e) => { markStart(); setForm({ ...form, name: e.target.value }); }} className={`mt-1 ${inputCls}`} placeholder="Vor- und Nachname" autoComplete="name" />
            </div>
            <div>
              <label htmlFor={`${testid}-tel`} className="text-xs font-semibold uppercase tracking-wider text-slate-500">Ihre Telefonnummer *</label>
              <input id={`${testid}-tel`} required type="tel" data-testid={`${testid}-phone-input`} value={form.telefon} onChange={(e) => { markStart(); setForm({ ...form, telefon: e.target.value }); }} className={`mt-1 ${inputCls}`} placeholder="Wir rufen Sie zurück" autoComplete="tel" />
            </div>
            <fieldset>
              <legend className="text-xs font-semibold uppercase tracking-wider text-slate-500">War der Unfall unverschuldet? *</legend>
              <div className="grid grid-cols-3 gap-2 mt-1.5">
                {SCHULD_OPTIONEN.map((o) => (
                  <button key={o.value} type="button" onClick={() => { markStart(); setForm({ ...form, schuld: o.value }); }} data-testid={`${testid}-schuld-${o.value}`}
                    className={`rounded-lg border-2 px-2 py-2.5 text-xs sm:text-sm font-bold transition-colors ${form.schuld === o.value ? "border-ember bg-ember/5 text-ember" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                    {o.label}
                  </button>
                ))}
              </div>
            </fieldset>
            <label className="flex items-start gap-2.5 text-xs text-slate-500 cursor-pointer" data-testid={`${testid}-datenschutz-label`}>
              <input type="checkbox" checked={datenschutz} onChange={(e) => setDatenschutz(e.target.checked)} data-testid={`${testid}-datenschutz-checkbox`} className="mt-0.5 h-4 w-4 accent-[#4A55B8]" />
              <span>Ich akzeptiere die <a href="/datenschutzerklaerung" className="underline">Datenschutzerklärung</a>. Keine Weitergabe an Dritte.</span>
            </label>
            <button type="submit" disabled={loading || !form.schuld} data-testid={`${testid}-submit-button`} className="cta-gradient w-full flex items-center justify-center gap-2 rounded-lg px-5 py-4 font-bold text-white text-base transition-transform duration-200 hover:scale-[1.02] disabled:opacity-60">
              {loading ? <Loader2 className="animate-spin" size={18} /> : <Phone size={18} />} Kostenlosen Rückruf anfordern
            </button>
            <div className="flex items-center justify-center gap-4 text-[11px] text-slate-400">
              <span className="flex items-center gap-1"><ShieldCheck size={12} /> SSL-verschlüsselt</span>
              <span>Kostenlos bei unverschuldetem Unfall</span>
            </div>
          </div>
        </form>
      ) : (
        <form onSubmit={submitExtra} className="p-6 sm:p-7" data-testid={`${testid}-step2`}>
          <div className="flex items-center gap-2 text-emerald-600">
            <CheckCircle2 size={20} />
            <h2 className="font-display text-xl font-bold text-navy">Geschafft – wir rufen Sie an!</h2>
          </div>
          <p className="text-sm text-slate-500 mt-1.5">Möchten Sie uns schon jetzt Details geben? <strong>Alles freiwillig</strong> – Sie können Unterlagen auch später nachreichen.</p>
          <div className="mt-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor={`${testid}-plz`} className="text-xs font-semibold uppercase tracking-wider text-slate-500">PLZ / Ort</label>
                <input id={`${testid}-plz`} data-testid={`${testid}-plz-input`} value={extra.plz} onChange={(e) => setExtra({ ...extra, plz: e.target.value })} className={`mt-1 ${inputCls}`} placeholder="z. B. 50823 Köln" />
              </div>
              <div>
                <label htmlFor={`${testid}-kennzeichen`} className="text-xs font-semibold uppercase tracking-wider text-slate-500">Kennzeichen</label>
                <input id={`${testid}-kennzeichen`} data-testid={`${testid}-kennzeichen-input`} value={extra.kennzeichen} onChange={(e) => setExtra({ ...extra, kennzeichen: e.target.value })} className={`mt-1 ${inputCls}`} placeholder="optional" />
              </div>
            </div>
            <div>
              <label htmlFor={`${testid}-termin`} className="text-xs font-semibold uppercase tracking-wider text-slate-500">Wunschtermin</label>
              <input id={`${testid}-termin`} data-testid={`${testid}-termin-input`} value={extra.wunschtermin} onChange={(e) => setExtra({ ...extra, wunschtermin: e.target.value })} className={`mt-1 ${inputCls}`} placeholder="z. B. morgen Nachmittag" />
            </div>
            <label htmlFor={`${testid}-fotos`} data-testid={`${testid}-fotos-upload`} className="flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-xl px-4 py-5 text-sm text-slate-500 cursor-pointer hover:border-ember hover:text-ember transition-colors">
              <Camera size={18} /> {fotos.length ? `${fotos.length} Foto(s) ausgewählt` : "Fotos vom Schaden (optional)"}
              <input id={`${testid}-fotos`} type="file" accept="image/*" multiple capture="environment" className="hidden" onChange={(e) => setFotos(Array.from(e.target.files || []).slice(0, 5))} />
            </label>
            <button type="submit" disabled={loading} data-testid={`${testid}-step2-submit`} className="cta-gradient w-full flex items-center justify-center gap-2 rounded-lg px-5 py-3.5 font-bold text-white disabled:opacity-60">
              {loading ? <Loader2 className="animate-spin" size={18} /> : <ArrowRight size={18} />} Angaben senden
            </button>
            <button type="button" onClick={() => navigate("/danke")} data-testid={`${testid}-step2-skip`} className="w-full text-center text-sm font-semibold text-slate-500 hover:text-ember py-1">
              Später nachreichen →
            </button>
          </div>
        </form>
      )}
    </div>
  );
};
